import { useCallback, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  CountrySelect,
  InfoBar,
  isTouchDevice,
  Link,
  LogSentryException,
  ServiceError,
  type ServiceErrorResponse,
  StateSelect,
  TextField,
} from "@shapr3d/frontend-lib";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import { checkoutBillingErrorContactUsURL } from "../../config";
import { useBilling, useUpdateBillingDetailsMutation } from "../../hooks/useBilling";
import type { BillingDetails } from "../../hooks/useCheckout";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { Modal } from "../../lib/modal";
import { ElementClickAction, ElementClickLocation } from "../../lib/TrackingAdapter";
import { ServiceErrorCode } from "../../types";
import { getVATNumberPrefix, isCountryInEU } from "../../utils/countryUtils";
import type { DeepMapNativeObjectKeysToValue } from "../../utils/typeUtils";
import { COUNTRIES_WITH_MANDATORY_STATE_SELECTOR } from "../ChargebeeCheckout/ChargebeeCheckoutSchema";

import style from "./BillingDetailsUpdateModal.module.css";

const createSchema = (
  t: ReturnType<typeof useTranslation>["t"],
  m: ReturnType<typeof useTranslation>["m"]
) =>
  z
    .object({
      company: z.string().trim().optional(),
      line1: z.string().trim().optional(),
      line2: z.string().trim().optional(),
      city: z.string().trim().optional(),
      countryCode: z.string().optional(),
      zip: z.string().trim().optional(),
      stateCode: z.string().optional(),
      vatNumber: z.string().trim().optional(),
    })
    .superRefine((val, ctx) => {
      if (val.vatNumber && !val.vatNumber?.startsWith(getVATNumberPrefix(val.countryCode))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          fatal: true,
          path: ["vatNumber"],
          message: t(m.TeamBilling.InvalidVatCountryCode),
        });

        return z.NEVER;
      }
    })
    .transform((val) => {
      if (val.vatNumber && isCountryInEU(val.countryCode)) {
        val.vatNumber = val.vatNumber.trim().slice(2);
      }
      return val;
    });

type BillingSchema = ReturnType<typeof createSchema>;

interface BillingDetailsUpdateModalProps {
  teamID: string;
  open: boolean;
  onClose: () => void;
}

export interface UpdateBillingDetailsServiceErrorResponse extends ServiceErrorResponse {
  validationErrors?: DeepMapNativeObjectKeysToValue<z.infer<BillingSchema>, { code: string }>;
}

export const BillingDetailsUpdateModal: React.FC<BillingDetailsUpdateModalProps> = ({
  teamID,
  open,
  onClose,
}) => {
  const { t, m } = useTranslation();
  const schema = useMemo(() => createSchema(t, m), [t, m]);
  const [isBillingUpdateError, setIsBillingUpdateError] = useState(false);
  const [isUnhandledError, setIsUnhandledError] = useState(false);
  const tracker = useTracker();

  const { data: billingData, isLoading: isBillingLoading } = useBilling(teamID);

  const {
    control,
    register,
    handleSubmit,
    reset,

    formState: { errors },
    setError,
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      ...billingData?.billingDetails,
      vatNumber: billingData?.billingDetails?.vatNumber
        ? `${getVATNumberPrefix(billingData.billingDetails.countryCode)}${
            billingData.billingDetails.vatNumber
          }`
        : undefined,
    },
    resolver: zodResolver(schema),
  });

  const countryCode = useWatch({ control, name: "countryCode" });

  const { mutate, isPending } = useUpdateBillingDetailsMutation(teamID);

  const close = useCallback(
    (values?: BillingDetails) => {
      values
        ? reset({
            ...values,
            vatNumber: values.vatNumber
              ? `${getVATNumberPrefix(values.countryCode)}${values.vatNumber}`
              : undefined,
          })
        : reset();
      onClose();
    },
    [onClose, reset]
  );

  const submit = useCallback(
    async (data: z.infer<typeof schema>) => {
      Object.entries(data).forEach(([key, value]) => {
        if (!value) {
          delete data[key as keyof typeof data];
        }
      });
      tracker.trackButtonClick({
        action: ElementClickAction.CONFIRM,
        location: ElementClickLocation.EDIT_BILLING_DETAILS_MODAL,
      });
      mutate(data, {
        onSuccess() {
          close(data);
        },
        onError(error) {
          if (error instanceof ServiceError && error.hasCode(ServiceErrorCode.ValidationError)) {
            const err: ServiceError = error;
            err.assertType<UpdateBillingDetailsServiceErrorResponse>();
            const validationErrors = Object.keys(err.serviceErrorResponse?.validationErrors ?? {});

            if (validationErrors.length > 0) {
              validationErrors.forEach((key) => {
                let message = t(m.TeamBilling.SomethingWentWrong);
                switch (key as keyof typeof data) {
                  case "company":
                    message = t(m.TeamBilling.InvalidName);
                    break;
                  case "line1":
                    message = t(m.TeamBilling.InvalidAddress);
                    break;
                  case "line2":
                    message = t(m.TeamBilling.InvalidAddress);
                    break;
                  case "city":
                    message = t(m.TeamBilling.InvalidCity);
                    break;
                  case "countryCode":
                    LogSentryException({
                      error: new Error("Invalid country during billing update"),
                      level: "error",
                    });
                    message = t(m.TeamBilling.InvalidCountry);
                    break;
                  case "zip":
                    message = t(m.TeamBilling.InvalidPostCode);
                    break;
                  case "stateCode":
                    LogSentryException({
                      error: new Error("Invalid state during billing update"),
                      level: "error",
                    });
                    message = t(m.TeamBilling.InvalidState);
                    break;
                  case "vatNumber":
                    message = t(m.TeamBilling.InvalidVatNumber);
                    break;
                  default:
                    LogSentryException({
                      error: new Error("Unknown error field during billing update", { cause: key }),
                      level: "error",
                    });
                    setIsUnhandledError(true);
                    return;
                }

                setError(key as keyof typeof data, {
                  message,
                  type: "validate",
                });
              });
              return;
            }

            setIsBillingUpdateError(true);
            return;
          }

          setIsUnhandledError(true);
        },
      });
    },
    [mutate, close, setIsBillingUpdateError, setIsUnhandledError, setError, tracker, t, m]
  );

  if (isBillingLoading) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        close();
      }}
      title={t(m.TeamBilling.EditBillingDetails)}
      telemetryName="billing_details_update"
    >
      <form className={style.form} onSubmit={handleSubmit(submit)}>
        <TextField
          {...register("company")}
          maxLength={250}
          errorMessage={errors.company?.message}
          placeholder={t(m.TeamBilling.NameOnBill)}
          autoFocus={!isTouchDevice()}
        />
        <TextField
          {...register("line1")}
          maxLength={150}
          errorMessage={errors.line1?.message}
          placeholder={t(m.TeamBilling.AddressLine1)}
        />
        <TextField
          {...register("line2")}
          maxLength={150}
          errorMessage={errors.line2?.message}
          placeholder={t(m.TeamBilling.AddressLine2)}
        />
        <TextField
          {...register("city")}
          maxLength={50}
          errorMessage={errors.city?.message}
          placeholder={t(m.TeamBilling.City)}
        />
        <div className={style.countryRow}>
          <CountrySelect {...register("countryCode")} errorMessage={errors.countryCode?.message} />
          <TextField
            {...register("zip")}
            maxLength={20}
            errorMessage={errors.zip?.message}
            placeholder={t(m.TeamBilling.PostCode)}
          />
        </div>
        {countryCode && COUNTRIES_WITH_MANDATORY_STATE_SELECTOR.includes(countryCode) && (
          <StateSelect
            {...register("stateCode")}
            errorMessage={errors.stateCode?.message}
            countryCode={countryCode as Parameters<typeof StateSelect>[0]["countryCode"]}
          />
        )}
        <TextField
          {...register("vatNumber")}
          placeholder={
            isCountryInEU(countryCode)
              ? t(m.TeamBilling.VatNumberPlaceholder)
              : t(m.TeamBilling.TaxIdPlaceholder)
          }
          errorMessage={errors.vatNumber?.message}
        />
        {isBillingUpdateError && (
          <InfoBar
            variant="critical"
            title={t(m.TeamBilling.BillingAddressIssue)}
            text={
              <>
                {t(m.TeamBilling.BillingVerificationError, {
                  contactLink: (chunks: React.ReactNode) => (
                    <Link href={checkoutBillingErrorContactUsURL} target="_blank">
                      {chunks}
                    </Link>
                  ),
                })}
              </>
            }
          />
        )}
        {isUnhandledError && (
          <InfoBar
            variant="warning"
            title={t(m.TeamBilling.UpdatingUnavailable)}
            text={t(m.TeamBilling.ReloadPageError)}
          />
        )}
        <div className={style.ctaRow}>
          <Button type="submit" disabled={isPending}>
            {t(m.TeamBilling.Save)}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
